import React, { useRef, useState, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize, Check, X, Magnet, Frame } from "lucide-react";
import { useCad } from "../context/CadContext";
import { getWorldPoints, pointInPolygon, nearestInsetPoint } from "../lib/shapes";
import { snapValue } from "../lib/inspect";
import { levelColor, EDGE_INSET } from "../constants/defaults";

export function Canvas2D() {
  const {
    layers, selectedId, setSelectedId, tool, addConnector, addCustomPlate,
    updatePlate, updateConnector, selectedConnector, setSelectedConnector, snap, setSnap,
  } = useCad();
  const ref = useRef(null);
  const [zoom, setZoom] = useState(4);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [readout, setReadout] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [draft, setDraft] = useState([]);
  const [measure, setMeasure] = useState({ a: null, b: null });
  const [cursor, setCursor] = useState(null);
  const drag = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const update = () => { const r = ref.current.getBoundingClientRect(); setSize({ w: r.width, h: r.height }); };
    update();
    const ro = new ResizeObserver(update); ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { if (tool !== "draw") setDraft([]); if (tool !== "measure") setMeasure({ a: null, b: null }); }, [tool]);

  const cx = size.w / 2 + pan.x;
  const cy = size.h / 2 + pan.y;
  const toScreen = (x, y) => ({ x: cx + x * zoom, y: cy - y * zoom });
  const toWorld = (sx, sy) => { const r = ref.current.getBoundingClientRect(); return { x: (sx - r.left - cx) / zoom, y: -(sy - r.top - cy) / zoom }; };
  const sn = (v) => snapValue(v, snap.size, snap.enabled);
  const gridPt = (p) => ({ x: +sn(p.x).toFixed(2), y: +sn(p.y).toFixed(2) });
  const snapForShape = (p, shape) => {
    if (snap.edge && shape) {
      const best = nearestInsetPoint(p, getWorldPoints(shape), EDGE_INSET);
      if (best && best.dist < 8) return { x: +best.x.toFixed(2), y: +best.y.toFixed(2) };
    }
    return gridPt(p);
  };

  const topShapeAt = (w) => {
    for (let i = layers.length - 1; i >= 0; i--) {
      if (layers[i].visible === false) continue;
      if (pointInPolygon(w.x, w.y, getWorldPoints(layers[i]))) return layers[i];
    }
    return null;
  };
  const hitConnector = (w) => {
    const th = 7 / zoom;
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i]; if (l.visible === false) continue;
      for (const cn of l.connectors) if (Math.hypot(cn.x - w.x, cn.y - w.y) <= th) return { plate: l, cn };
    }
    return null;
  };

  const onPointerDown = (e) => {
    const w = toWorld(e.clientX, e.clientY);
    if (tool === "draw") { setDraft((d) => [...d, gridPt(w)]); return; }
    if (tool === "measure") {
      const p = gridPt(w);
      setMeasure((m) => (!m.a || (m.a && m.b) ? { a: p, b: null } : { a: m.a, b: p }));
      return;
    }
    const hit = topShapeAt(w);
    if (tool === "connector") {
      const target = hit || (selectedId ? layers.find((l) => l.id === selectedId) : null);
      if (target) { const p = snapForShape(w, target); addConnector(target.id, p.x, p.y); setSelectedId(target.id); }
      return;
    }
    const ch = hitConnector(w);
    if (ch) {
      setSelectedId(ch.plate.id); setSelectedConnector(ch.cn.id);
      drag.current = { type: "connector", plate: ch.plate, cnId: ch.cn.id };
      e.currentTarget.setPointerCapture(e.pointerId); return;
    }
    if (hit) {
      setSelectedId(hit.id); setSelectedConnector(null);
      drag.current = { type: "move", id: hit.id, startW: w, orig: { x: hit.x, y: hit.y }, united: hit.united, origConns: hit.connectors.map((c) => ({ ...c })) };
    } else {
      drag.current = { type: "pan", startS: { x: e.clientX, y: e.clientY }, orig: { ...pan } };
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const w = toWorld(e.clientX, e.clientY);
    setReadout(w);
    if (tool === "measure") setCursor(gridPt(w));
    const d = drag.current; if (!d) return;
    if (d.type === "pan") setPan({ x: d.orig.x + (e.clientX - d.startS.x), y: d.orig.y + (e.clientY - d.startS.y) });
    else if (d.type === "move") {
      const p = gridPt({ x: d.orig.x + (w.x - d.startW.x), y: d.orig.y + (w.y - d.startW.y) });
      const patch = { x: p.x, y: p.y };
      if (d.united) {
        const dx = p.x - d.orig.x, dy = p.y - d.orig.y;
        patch.connectors = d.origConns.map((c) => ({ ...c, x: +(c.x + dx).toFixed(2), y: +(c.y + dy).toFixed(2) }));
      }
      updatePlate(d.id, patch);
    } else if (d.type === "connector") {
      const p = snapForShape(w, d.plate);
      updateConnector(d.plate.id, d.cnId, { x: p.x, y: p.y });
    }
  };
  const onPointerUp = () => { drag.current = null; };
  const onWheel = (e) => setZoom((z) => Math.min(40, Math.max(1, z * (e.deltaY < 0 ? 1.12 : 0.89))));

  const finishDraft = () => { if (draft.length >= 3) addCustomPlate(draft); setDraft([]); };
  const fit = () => { setPan({ x: 0, y: 0 }); setZoom(4); };

  const W = size.w || 800, H = size.h || 600;
  const gridStep = 10 * zoom;
  const lines = [];
  if (gridStep > 4) {
    for (let x = ((cx % gridStep) + gridStep) % gridStep; x < W; x += gridStep) lines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} stroke="#152036" strokeWidth={1} />);
    for (let y = ((cy % gridStep) + gridStep) % gridStep; y < H; y += gridStep) lines.push(<line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} stroke="#152036" strokeWidth={1} />);
  }
  const origin = toScreen(0, 0);
  const cursorClass = tool === "connector" || tool === "draw" || tool === "measure" ? "cursor-crosshair" : "cursor-default";

  const mA = measure.a && toScreen(measure.a.x, measure.a.y);
  const mEnd = measure.b || (measure.a && cursor);
  const mB = mEnd && toScreen(mEnd.x, mEnd.y);
  const mDist = measure.a && mEnd ? Math.hypot(mEnd.x - measure.a.x, mEnd.y - measure.a.y) : 0;

  return (
    <div className="relative h-full w-full bg-[#070B12] overflow-hidden select-none">
      <div className="absolute top-2.5 left-3 z-10 text-[11px] font-mono text-slate-500 uppercase tracking-widest">2D · Top-Down</div>
      <div ref={ref} data-testid="canvas-2d" className={`absolute inset-0 ${cursorClass}`}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        onDoubleClick={() => tool === "draw" && finishDraft()} onWheel={onWheel}>
        <svg width="100%" height="100%" className="block">
          {lines}
          <line x1={0} y1={origin.y} x2={W} y2={origin.y} stroke="#22C55E" strokeWidth={1} strokeOpacity={0.5} />
          <line x1={origin.x} y1={0} x2={origin.x} y2={H} stroke="#EF4444" strokeWidth={1} strokeOpacity={0.5} />

          {layers.map((plate) => {
            if (plate.visible === false) return null;
            const col = levelColor(plate.level);
            const pts = getWorldPoints(plate).map((p) => toScreen(p.x, p.y));
            const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + " Z";
            const sel = plate.id === selectedId;
            return (
              <g key={plate.id} data-testid={`canvas-plate-${plate.id}`}>
                <path d={d} fill={col} fillOpacity={sel ? 0.3 : 0.15} stroke={col} strokeWidth={sel ? 2.2 : 1.4} strokeOpacity={sel ? 1 : 0.7} />
                {plate.connectors.map((cn) => {
                  const s = toScreen(cn.x, cn.y);
                  const active = cn.id === selectedConnector;
                  return (
                    <g key={cn.id} data-testid={`canvas-connector-${cn.id}`} style={{ cursor: "grab" }}>
                      <circle cx={s.x} cy={s.y} r={active ? 6 : 4.5} fill={col} stroke="#0B1120" strokeWidth={1.5} />
                      <circle cx={s.x} cy={s.y} r={active ? 11 : 8} fill="none" stroke={col} strokeOpacity={0.5} strokeWidth={1} />
                    </g>
                  );
                })}
              </g>
            );
          })}

          {draft.length > 0 && (
            <g>
              <polyline points={draft.map((p) => { const s = toScreen(p.x, p.y); return `${s.x},${s.y}`; }).join(" ")} fill="none" stroke="#10B981" strokeWidth={1.6} strokeDasharray="4 3" />
              {draft.map((p, i) => { const s = toScreen(p.x, p.y); return <circle key={i} cx={s.x} cy={s.y} r={3.5} fill="#10B981" />; })}
            </g>
          )}

          {mA && mB && (
            <g>
              <line x1={mA.x} y1={mA.y} x2={mB.x} y2={mB.y} stroke="#F59E0B" strokeWidth={1.6} strokeDasharray="5 3" />
              <circle cx={mA.x} cy={mA.y} r={3.5} fill="#F59E0B" />
              <circle cx={mB.x} cy={mB.y} r={3.5} fill="#F59E0B" />
              <rect x={(mA.x + mB.x) / 2 - 26} y={(mA.y + mB.y) / 2 - 20} width={52} height={16} rx={4} fill="#0B1120" stroke="#F59E0B" strokeOpacity={0.5} />
              <text x={(mA.x + mB.x) / 2} y={(mA.y + mB.y) / 2 - 8} textAnchor="middle" fontSize="10" fill="#F59E0B" fontFamily="JetBrains Mono, monospace">{mDist.toFixed(2)}mm</text>
            </g>
          )}
        </svg>
      </div>

      {tool === "draw" && (
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-slate-900/90 border border-slate-700 rounded-full px-3 py-1.5">
          <span className="text-[11px] text-emerald-300 font-mono">Custom draw · {draft.length} pts</span>
          <button data-testid="draw-finish-btn" onClick={finishDraft} disabled={draft.length < 3} className="h-6 px-2 rounded-full bg-emerald-500 text-white text-[11px] flex items-center gap-1 disabled:opacity-40"><Check size={12} /> Finish</button>
          <button data-testid="draw-cancel-btn" onClick={() => setDraft([])} className="h-6 w-6 grid place-items-center rounded-full bg-slate-800 text-slate-300 hover:text-white"><X size={12} /></button>
        </div>
      )}
      {tool === "measure" && (
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-slate-900/90 border border-amber-700/60 rounded-full px-3 py-1.5">
          <Frame size={13} className="text-amber-400" />
          <span className="text-[11px] text-amber-300 font-mono">{measure.a && measure.b ? `${mDist.toFixed(2)}mm — click to restart` : measure.a ? "click second point" : "click first point"}</span>
        </div>
      )}

      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5">
        <IconBtn testid="zoom-in-btn" onClick={() => setZoom((z) => Math.min(40, z * 1.2))}><ZoomIn size={15} /></IconBtn>
        <IconBtn testid="zoom-out-btn" onClick={() => setZoom((z) => Math.max(1, z / 1.2))}><ZoomOut size={15} /></IconBtn>
        <IconBtn testid="fit-view-btn" onClick={fit}><Maximize size={15} /></IconBtn>
        <button data-testid="grid-snap-toggle" onClick={() => setSnap((s) => ({ ...s, enabled: !s.enabled }))}
          className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-[11px] font-mono transition-colors duration-150 ${snap.enabled ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-slate-900/70 text-slate-400 border-slate-800 hover:text-white"}`}>
          <Magnet size={13} /> Grid
        </button>
        <input data-testid="grid-size-input" type="number" step={0.5} min={0.1} value={snap.size}
          onChange={(e) => setSnap((s) => ({ ...s, size: Math.max(0.1, Number(e.target.value) || 1) }))}
          className="h-8 w-14 rounded-md bg-slate-900/70 border border-slate-800 px-2 text-[11px] font-mono text-slate-200 focus:border-emerald-500 focus:outline-none" />
        <button data-testid="edge-snap-toggle" onClick={() => setSnap((s) => ({ ...s, edge: !s.edge }))}
          className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md border text-[11px] font-mono transition-colors duration-150 ${snap.edge ? "bg-sky-500/15 text-sky-400 border-sky-500/40" : "bg-slate-900/70 text-slate-400 border-slate-800 hover:text-white"}`}>
          Edge {EDGE_INSET}mm
        </button>
      </div>
      <div className="absolute bottom-3 right-3 z-10 text-[11px] font-mono text-slate-500 bg-slate-900/60 px-2 py-1 rounded border border-slate-800">
        X {readout.x.toFixed(1)} · Y {readout.y.toFixed(1)} mm
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="h-8 w-8 grid place-items-center rounded-md bg-slate-900/70 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors duration-150">{children}</button>
);
